/**
 * TRUS-513 — where the Super Admin role may be offered in the UI.
 *
 * Super Admin reads and writes every organization's data. The invite dropdowns
 * used to offer it in whatever org the admin happened to be looking at, which is
 * how it got handed out by mistake: the option sat one line above the ordinary
 * org roles, and (worse) was pre-selected as the default for super admins.
 *
 * Two rules, mirrored server-side in `app/utils/super_admin_policy.py`:
 *
 *  1. The option appears ONLY in the platform organization (CBO 1), where it is
 *     labelled "Platform Admin" — nowhere else, for anybody.
 *  2. It is never the default selection. Picking it is a deliberate act, and it
 *     only raises a request: a second super admin has to approve it before the
 *     invitation is sent.
 *
 * The backend enforces all of this independently (400 outside CBO 1, 403 for an
 * ineligible address, and the invitation is parked at `pending_approval`); this
 * module is the affordance, not the control.
 */

/** The platform organization. Super Admin is granted here and nowhere else. */
export const PLATFORM_ORG_ID = 1;

/** System roles that must never appear as an ordinary org-role option. */
export const INTERNAL_SYSTEM_ROLES = ['super_admin', 'ops_admin'] as const;

export const isPlatformOrg = (cboId: number | string | null | undefined): boolean =>
  Number(cboId) === PLATFORM_ORG_ID;

/**
 * May this caller be offered the Super Admin ("Platform Admin") option here?
 * Only a super admin, and only inside the platform organization.
 */
export const canOfferSuperAdmin = (
  cboId: number | string | null | undefined,
  effectiveSystemRole: string | null | undefined,
): boolean => isPlatformOrg(cboId) && effectiveSystemRole === 'super_admin';

/**
 * The label for the Super Admin option. The ticket asks for it to read as
 * "Platform Admin" in CBO 1 — that is the name the team uses for the tier, and
 * it keeps "Super Admin" out of role pickers entirely.
 */
export const SUPER_ADMIN_OPTION_LABEL = 'Platform Admin (Super Admin — needs approval)';

/** True for an org role that is really an internal tier and must be hidden. */
export const isInternalSystemRole = (systemRole: string | null | undefined): boolean =>
  !!systemRole && (INTERNAL_SYSTEM_ROLES as readonly string[]).includes(systemRole);
